import { Mic, Users, Church } from "lucide-react";
import PartsEditor from "@/components/creator/music/PartsEditor";
import { DEFAULT_ARRANGEMENT, normalizeParts } from "@/lib/arrangement";
import { shortVoiceName } from "@/lib/studioApi";

const ICONS = { solo: Mic, duet: Users, choir: Church };

function PartnerPicker({ partner, profiles, onChange }) {
  const guests = (profiles || []).filter((p) => !p.is_main);
  const value = partner.kind === "profile" ? `profile:${partner.profile_id || ""}` : partner.kind;
  const pick = (v) => onChange(v.startsWith("profile:") ? { kind: "profile", profile_id: v.slice(8) || null } : { kind: v, profile_id: null });
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/55">Duet partner</span>
      <select data-testid="arrangement-partner-select" value={value} onChange={(e) => pick(e.target.value)} className="w-full rounded-xl border border-white/10 bg-black/50 px-3 py-2 text-sm text-white focus:border-blue-400/60 focus:outline-none">
        <option value="female">Female gospel voice (AI)</option>
        <option value="male">Male gospel voice (AI)</option>
        {guests.map((g) => <option key={g.id} value={`profile:${g.id}`}>{shortVoiceName(g.name)} (guest profile)</option>)}
      </select>
    </label>
  );
}

/** Solo, duet (me + AI partner) or lead with choir call & response — and who sings each section. */
export default function ArrangementPanel({ catalog, form, profiles, onChange }) {
  const arr = { ...DEFAULT_ARRANGEMENT, ...(form.arrangement || {}) };
  const parts = normalizeParts(form.lyrics, arr);
  const set = (patch) => onChange({ arrangement: { ...arr, ...patch } });
  const kinds = catalog.arrangements || [];
  const current = kinds.find((k) => k.key === arr.type);
  const lead = (profiles || []).find((p) => (form.vocal?.profile_id ? p.id === form.vocal.profile_id : p.is_main));
  const leadFemale = form.vocal?.mode === "female" || (form.vocal?.mode === "signature" && lead?.gender === "f");
  const choose = (type) => {
    const partner = type === "duet" && arr.type !== "duet" && arr.partner?.kind !== "profile" ? { kind: leadFemale ? "male" : "female", profile_id: null } : arr.partner;
    set({ type, parts: [], partner });
  };
  return (
    <section data-testid="arrangement-panel">
      <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.16em] text-white/70">Arrangement</p>
      <div className="grid gap-2 sm:grid-cols-3">
        {kinds.map((k) => {
          const Icon = ICONS[k.key] || Mic;
          const active = arr.type === k.key;
          return (
            <button key={k.key} type="button" data-testid={`arrangement-${k.key}`} aria-pressed={active} onClick={() => choose(k.key)}
              className={`rounded-2xl border px-3.5 py-3 text-left transition-colors duration-200 ${active ? "border-blue-300/70 bg-blue-500/20 text-white" : "border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.07]"}`}>
              <Icon className="mb-1.5 h-4 w-4" />
              <p className="font-display text-[13px] font-semibold">{k.label}</p>
              <p className="mt-0.5 text-[11px] leading-snug text-white/50">{k.hint}</p>
            </button>
          );
        })}
      </div>
      {arr.type !== "solo" ? (
        <div className="mt-4 space-y-4 rounded-3xl border border-white/[0.08] bg-black/25 p-4">
          {arr.type === "duet" ? <PartnerPicker partner={arr.partner || DEFAULT_ARRANGEMENT.partner} profiles={profiles} onChange={(partner) => set({ partner })} /> : null}
          <PartsEditor parts={parts} singers={current?.singers || []} onChange={(next) => set({ parts: next })} />
          <p className="text-[11px] leading-relaxed text-white/45">
            Only the [section] labels get the singer names — every word you wrote is still sung exactly as typed.
            {arr.type === "duet" ? " After the song is made, “Sing it myself” plays the partner's parts so you can record yours." : ""}
          </p>
        </div>
      ) : null}
    </section>
  );
}
